import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { MonthlySummary, UsageReport as Report, UsageRow, performanceApi } from '../../services/performanceApi';
import { SectionTitle, StatCard, fmtBucket, fmtCredits, fmtMs, fmtNum, fmtPct } from './common';

const lastMonths = (n: number) => {
  const out: string[] = [];
  const d = new Date();
  d.setUTCDate(1);
  for (let i = 0; i < n; i += 1) {
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
    d.setUTCMonth(d.getUTCMonth() - 1);
  }
  return out;
};

const monthLabel = (m: string) =>
  new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });

const UsageTable: React.FC<{ rows: UsageRow[]; keyLabel: string; keyOf: (r: UsageRow) => string }> = ({ rows, keyLabel, keyOf }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{keyLabel}</TableCell>
          <TableCell align="right">Runs</TableCell>
          <TableCell align="right">Success</TableCell>
          <TableCell align="right">
            <Tooltip title="Typical run (p50), and the times only 5% / 1% of successful runs exceeded">
              <span style={{ cursor: 'help' }}>p50 / p95 / p99</span>
            </Tooltip>
          </TableCell>
          <TableCell align="right">
            <Tooltip title="Runs answered by a backup model because the first model failed or was too slow">
              <span style={{ cursor: 'help' }}>Backup</span>
            </Tooltip>
          </TableCell>
          <TableCell align="right">Timed out</TableCell>
          <TableCell align="right">Invalid output</TableCell>
          <TableCell align="right">Credits</TableCell>
          <TableCell align="right">Credits / 1k runs</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={9} align="center">
              No runs in this period
            </TableCell>
          </TableRow>
        ) : (
          rows.map(r => (
            <TableRow key={keyOf(r)} hover>
              <TableCell>
                {keyOf(r)}
                {r.usage_only > 0 && (
                  <Tooltip title="Runs where input and output weren't stored. They're still counted here.">
                    <Chip size="small" variant="outlined" label={`${fmtNum(r.usage_only)} usage-only`} sx={{ ml: 1 }} />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell align="right">{fmtNum(r.runs)}</TableCell>
              <TableCell align="right">{fmtPct(r.success_rate)}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {fmtMs(r.p50_ms)} / {fmtMs(r.p95_ms)} / {fmtMs(r.p99_ms)}
              </TableCell>
              <TableCell align="right">{fmtPct(r.backup_share)}</TableCell>
              <TableCell align="right">{fmtPct(r.timeout_share)}</TableCell>
              <TableCell align="right">{fmtPct(r.invalid_output_share)}</TableCell>
              <TableCell align="right">{fmtCredits(r.credits)}</TableCell>
              <TableCell align="right">{fmtCredits(r.credits_per_1k_runs)}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

export const UsageReport: React.FC<{ customerId?: string | null }> = ({ customerId }) => {
  const { getIdToken } = useAuth();
  const api = useMemo(() => performanceApi(getIdToken), [getIdToken]);
  const months = useMemo(() => lastMonths(12), []);

  const [days, setDays] = useState(30);
  const [groupBy, setGroupBy] = useState<'function' | 'model'>('function');
  const [report, setReport] = useState<Report | null>(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [month, setMonth] = useState(months[0]);
  const [monthly, setMonthly] = useState<MonthlySummary | null>(null);
  const [monthlyLoading, setMonthlyLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setReportLoading(true);
    api
      .usage(days, groupBy, customerId)
      .then(r => !cancelled && setReport(r))
      .catch(e => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load report'))
      .finally(() => !cancelled && setReportLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, days, groupBy, customerId]);

  useEffect(() => {
    let cancelled = false;
    setMonthlyLoading(true);
    api
      .monthly(month, customerId)
      .then(r => !cancelled && setMonthly(r))
      .catch(e => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load monthly summary'))
      .finally(() => !cancelled && setMonthlyLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, month, customerId]);

  const download = async (which: 'usage' | 'monthly') => {
    setDownloading(which);
    try {
      if (which === 'usage') await api.usageCsv(days, groupBy, customerId);
      else await api.monthlyCsv(month, customerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const daily = (monthly?.daily ?? []).map(d => ({ label: fmtBucket(d.bucket), runs: d.runs, failed: d.runs - d.ok }));
  const t = monthly?.totals;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 4 }}>
        <SectionTitle
          title="Performance report"
          help="Speed, reliability and cost per function or model. Usage-only runs (content not stored) are included."
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <ToggleButtonGroup size="small" exclusive value={groupBy} onChange={(_, v) => v && setGroupBy(v)}>
                <ToggleButton value="function">By function</ToggleButton>
                <ToggleButton value="model">By model</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
                {[7, 30, 90].map(d => (
                  <ToggleButton key={d} value={d}>
                    {d}d
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Button
                size="small"
                variant="outlined"
                startIcon={downloading === 'usage' ? <CircularProgress size={14} /> : <DownloadIcon />}
                onClick={() => download('usage')}
                disabled={!!downloading}
              >
                CSV
              </Button>
            </Box>
          }
        />
        <Box sx={{ opacity: reportLoading ? 0.5 : 1, minHeight: 80 }}>
          {report ? (
            <UsageTable
              rows={report.rows}
              keyLabel={groupBy === 'function' ? 'Function' : 'Model'}
              keyOf={r => (groupBy === 'function' ? r.function_name || '–' : r.model_used || '–')}
            />
          ) : (
            reportLoading && <CircularProgress size={24} />
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <SectionTitle
          title="Monthly summary"
          help={monthly && !monthly.complete ? 'This month so far (UTC).' : 'Calendar month (UTC).'}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Month</InputLabel>
                <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
                  {months.map(m => (
                    <MenuItem key={m} value={m}>
                      {monthLabel(m)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="outlined"
                startIcon={downloading === 'monthly' ? <CircularProgress size={14} /> : <DownloadIcon />}
                onClick={() => download('monthly')}
                disabled={!!downloading}
              >
                CSV
              </Button>
            </Box>
          }
        />
        <Box sx={{ opacity: monthlyLoading ? 0.5 : 1 }}>
          {!t || t.runs === 0 ? (
            !monthlyLoading && <Alert severity="info">No runs in {monthLabel(month)}.</Alert>
          ) : (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} md={2.4}>
                  <StatCard label="Runs" value={fmtNum(t.runs)} />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <StatCard label="Success rate" value={fmtPct(t.success_rate)} caption={`${fmtNum(t.runs - t.ok)} failed`} />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <StatCard label="p95 time" value={fmtMs(t.p95_ms)} caption={`typical ${fmtMs(t.p50_ms)}`} />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <StatCard label="Answered by backup" value={fmtPct(t.backup_share)} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <StatCard label="AI credits" value={fmtCredits(t.credits)} caption={`${fmtCredits(t.credits_per_1k_runs)} per 1,000 runs`} />
                </Grid>
              </Grid>
              <Box sx={{ height: 180, mb: 2 }}>
                <ResponsiveContainer>
                  <BarChart data={daily} margin={{ top: 4, right: 0, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={12} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <ChartTooltip />
                    <Bar dataKey="runs" name="Runs" fill="#1976d2" />
                    <Bar dataKey="failed" name="Failed" fill="#e57373" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <UsageTable rows={monthly!.functions} keyLabel="Function" keyOf={r => r.function_name || '–'} />
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
